import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowRight,
  Database,
  FileText,
  LayoutDashboard,
  Target,
} from "lucide-react";
import { Link } from "react-router";

const flowData = [
  {
    unsur: "Unsur Pelaksanaan Pendidikan (A)",
    sumber: ["SIAK", "SHAKUNTALA"],
    basis: ["SK", "AGENDA"],
  },
  {
    unsur: "Membimbing Seminar Proposal (TA, Skripsi, Tesis, Disertasi)",
    sumber: ["SHAKUNTALA"],
    basis: ["SK"],
  },
  {
    unsur: "Menguji Proposal (TA, Skripsi, Tesis, Disertasi)",
    sumber: ["SHAKUNTALA"],
    basis: ["SK"],
  },
  {
    unsur: "Membimbing TA/Skripsi/Tesis/Disertasi",
    sumber: ["SHAKUNTALA"],
    basis: ["SK"],
  },
  {
    unsur: "Menguji TA, Skripsi, Tesis, dan Disertasi",
    sumber: ["SHAKUNTALA"],
    basis: ["SK"],
  },
  {
    unsur: "Pembimbing Akademik, Kemahasiswaan, Dosen Muda",
    sumber: ["SHAKUNTALA"],
    basis: ["SK"],
  },
  {
    unsur: "Unsur Penunjang (D)",
    sumber: ["SHAKUNTALA"],
    basis: ["SK"],
  },
  {
    unsur: "Tambahan Kinerja Dosen dalam Jabatan Tambahan",
    sumber: ["SHAKUNTALA", "SDM"],
    basis: ["SK"],
  },
  {
    unsur: "Produk Penelitian, Pengabdian Kepada Masyarakat & Produk Inovasi",
    sumber: ["KINERJA"],
    basis: ["KARYA"],
  },
];

const FlowArrow = () => (
  <div className="flex-1 flex items-center justify-center min-w-10 px-2 relative md:flex">
    <svg className="w-full h-8" viewBox="0 0 100 20" preserveAspectRatio="none">
      <path
        d="M0,10 C30,10 70,10 100,10"
        stroke="#cbd5e1"
        strokeWidth="2"
        fill="none"
        strokeDasharray="4 4"
      />
      <circle r="4" fill="#3b82f6" className="drop-shadow-md">
        <animateMotion
          dur="2s"
          repeatCount="indefinite"
          path="M0,10 C30,10 70,10 100,10"
        />
      </circle>
    </svg>
    {/* Arrow head */}
    <div className="absolute right-0 text-slate-300">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    </div>
  </div>
);

export default function DosenDashboardPage() {
  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-primary/10 rounded-xl text-primary">
            <LayoutDashboard className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">
              Dashboard Dosen
            </h1>
            <p className="text-slate-500 text-sm">
              Aliran Data Sistem Remunerasi Terintegrasi
            </p>
          </div>
        </div>
        <Link to="/dosen/remunerasi">
          <Button className="gap-2 w-full sm:w-auto shadow-sm">
            Lihat Data Remunerasi <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>

      <Card className="shadow-sm border-slate-200">
        <CardHeader className="bg-slate-50/50 border-b border-slate-100">
          <CardTitle className="text-lg text-slate-700 flex items-center gap-2">
            <Database className="w-5 h-5 text-indigo-500" />
            Integrasi Sumber Data & Unsur Kinerja
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {/* Header Legend */}
          <div className="grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1.5fr] gap-4 mb-6 text-sm font-semibold text-slate-500 uppercase tracking-wider text-center md:text-left hidden md:grid">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4" /> Sistem Sumber
            </div>
            <div className="w-20"></div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" /> Jenis Dokumen (Bukti)
            </div>
            <div className="w-20"></div>
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4" /> Komponen Kinerja (Unsur)
            </div>
          </div>

          <div className="space-y-4">
            {flowData.map((item, idx) => (
              <div
                key={idx}
                className="flex flex-col md:flex-row items-center gap-4 p-4 rounded-xl border border-slate-100 bg-white hover:bg-slate-50 hover:border-slate-200 transition-all shadow-sm hover:shadow-md group"
              >
                {/* Sumber */}
                <div className="w-full md:w-auto md:flex-1 flex flex-wrap gap-2 items-center justify-center md:justify-start">
                  {item.sumber.map((s) => (
                    <Badge
                      key={s}
                      variant="outline"
                      className="bg-indigo-50 text-indigo-700 border-indigo-200 font-medium px-3 py-1 shadow-sm uppercase"
                    >
                      {s}
                    </Badge>
                  ))}
                </div>

                <FlowArrow />

                {/* Basis */}
                <div className="w-full md:w-auto md:flex-1 flex flex-wrap gap-2 items-center justify-center md:justify-start">
                  {item.basis.map((b) => (
                    <Badge
                      key={b}
                      variant="secondary"
                      className="bg-amber-50 text-amber-700 border border-amber-200 font-medium px-3 py-1 shadow-sm uppercase"
                    >
                      {b}
                    </Badge>
                  ))}
                </div>

                <FlowArrow />

                {/* Unsur */}
                <div className="w-full md:w-auto md:flex-[1.5] flex items-center justify-center md:justify-start text-center md:text-left">
                  <div className="font-semibold text-slate-700 text-sm leading-tight group-hover:text-primary transition-colors">
                    {item.unsur}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
